/* ============================================
   DNS Instructions — Inline guide for domain setup
   Shows nameservers and A record options with copy buttons
   ============================================ */

import { useState } from 'react'

interface DnsInstructionsProps {
  dominio: string
  ipServidor?: string
  isExpanded?: boolean
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* silent */ }
  }

  return (
    <button
      onClick={handleCopy}
      title="Copiar"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 6px',
        borderRadius: 4,
        color: copied ? 'var(--success)' : 'var(--text-secondary)',
        fontSize: '0.75rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        transition: 'color 0.2s',
      }}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  )
}

const NAMESERVERS = [
  'ns1.mysecurecloudhost.com',
  'ns2.mysecurecloudhost.com',
  'ns3.mysecurecloudhost.com',
  'ns4.mysecurecloudhost.com',
]

const SERVER_IP = '190.92.174.30'

export default function DnsInstructions({ dominio, ipServidor, isExpanded: defaultExpanded = false }: DnsInstructionsProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const ip = ipServidor || SERVER_IP

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        style={{
          background: 'color-mix(in srgb, var(--warning) 10%, transparent)',
          border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
          borderRadius: 8,
          padding: '8px 14px',
          cursor: 'pointer',
          color: 'var(--warning)',
          fontSize: '0.8rem',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        DNS pendiente — Clic para ver instrucciones
      </button>
    )
  }

  return (
    <div
      style={{
        background: 'color-mix(in srgb, var(--warning) 6%, transparent)',
        border: '1px solid color-mix(in srgb, var(--warning) 20%, transparent)',
        borderRadius: 10,
        padding: '16px',
        fontSize: '0.85rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--warning)', fontWeight: 600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Configurar DNS para {dominio}
        </div>
        <button
          onClick={() => setExpanded(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Option 1: Nameservers */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>
          Opción 1 — Nameservers (recomendado)
        </div>
        <div style={{
          background: 'var(--card-bg, #1a1a2e)',
          borderRadius: 8,
          padding: '10px 12px',
        }}>
          {NAMESERVERS.map(ns => (
            <div key={ns} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
              <code style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{ns}</code>
              <CopyButton text={ns} />
            </div>
          ))}
        </div>
      </div>

      {/* Option 2: A Record */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 }}>
          Opción 2 — Registros A
        </div>
        <div style={{
          background: 'var(--card-bg, #1a1a2e)',
          borderRadius: 8,
          padding: '10px 12px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
            <span style={{ fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Tipo:</span> A &nbsp;
              <span style={{ color: 'var(--text-secondary)' }}>Host:</span> @ &nbsp;
              <span style={{ color: 'var(--text-secondary)' }}>Valor:</span> <code>{ip}</code>
            </span>
            <CopyButton text={ip} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
            <span style={{ fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Tipo:</span> A &nbsp;
              <span style={{ color: 'var(--text-secondary)' }}>Host:</span> www &nbsp;
              <span style={{ color: 'var(--text-secondary)' }}>Valor:</span> <code>{ip}</code>
            </span>
            <CopyButton text={ip} />
          </div>
        </div>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', lineHeight: 1.5 }}>
        Los cambios de DNS pueden tardar de 2 a 48 horas en propagarse globalmente.
      </div>
    </div>
  )
}
